((root, factory) => {
  const config = factory();
  if (typeof module === 'object' && module.exports) module.exports = config;
  root.CorkopiaProductConfig = config;
})(typeof globalThis !== 'undefined' ? globalThis : window, () => {
  'use strict';

  const config = {
    schemaVersion: '1.0.0',
    rawShopifySchemaVersion: 'shopify-liquid-v1',

    families: {
      cork_flat: { label: 'Cork Flat', aliases: ['cork_flat', 'cork-flat', 'cork flat', 'flat', 'flats'] },
      cork_round: { label: 'Cork Round', aliases: ['cork_round', 'cork-round', 'cork round', 'round', 'rounds'] },
      half_round: { label: 'Half Round', aliases: ['half_round', 'half-round', 'half round'] },
      branch: { label: 'Branch', aliases: ['branch', 'branches', 'cork_branch', 'cork branch'] },
      statement_piece: { label: 'Statement Piece', aliases: ['statement_piece', 'statement-piece', 'statement piece', 'statement'] },
      background_piece: { label: 'Background Piece', aliases: ['background_piece', 'background-piece', 'background piece', 'background'] }
    },

    inventoryTypes: {
      exact: ['individual_piece', 'individual', 'exact_piece'],
      prebuilt: ['prebuilt_box', 'prebuilt', 'assortment']
    },

    orientations: {
      free: 'May rotate',
      upright: 'Keep upright',
      fixed: 'Fixed orientation'
    },

    sizeClasses: {
      approved: false,
      values: {
        xs: 'Extra Small',
        small: 'Small',
        medium: 'Medium',
        large: 'Large',
        xl: 'Extra Large',
        oversized: 'Oversized'
      },
      thresholds: [],
      note: 'Thresholds intentionally remain empty until the owner approves longest-dimension and/or volume bands.'
    },

    measurementUnits: {
      length: {
        target: 'in',
        factors: {
          in: 1,
          inch: 1,
          inches: 1,
          ft: 12,
          foot: 12,
          feet: 12,
          mm: 0.0393700787,
          cm: 0.3937007874,
          m: 39.37007874
        }
      },
      weight: {
        target: 'oz',
        factors: {
          oz: 1,
          ounce: 1,
          ounces: 1,
          lb: 16,
          lbs: 16,
          pound: 16,
          pounds: 16,
          g: 0.0352739619,
          gram: 0.0352739619,
          grams: 0.0352739619,
          kg: 35.2739619
        }
      },
      area: {
        target: 'sq_in',
        factors: {
          sq_in: 1,
          in2: 1,
          'in²': 1,
          sq_ft: 144,
          ft2: 144,
          'ft²': 144,
          sq_cm: 0.15500031,
          cm2: 0.15500031,
          'cm²': 0.15500031
        }
      },
      volume: {
        target: 'cu_in',
        factors: {
          cu_in: 1,
          in3: 1,
          'in³': 1,
          ml: 0.0610237441,
          l: 61.0237441,
          liter: 61.0237441,
          liters: 61.0237441,
          cm3: 0.0610237441,
          'cm³': 0.0610237441,
          cu_ft: 1728,
          ft3: 1728,
          'ft³': 1728
        }
      }
    },

    compatibility: {
      approved: false,
      animal: {
        labels: {},
        note: 'Animal taxonomy requires owner approval. Values are preserved as handles and reported as unapproved.'
      },
      enclosure: {
        labels: {},
        note: 'Enclosure taxonomy and dimensional rules require owner approval.'
      },
      humidity: {
        labels: {
          dry: 'Dry',
          moderate: 'Moderate humidity',
          humid: 'High humidity',
          variable: 'Variable humidity'
        }
      },
      buildRole: {
        labels: {
          focal: 'Focal point',
          hide: 'Hide',
          climb: 'Climbing structure',
          bridge: 'Bridge',
          background: 'Background structure',
          mount: 'Mounting surface'
        }
      }
    },

    packing: {
      safeVolumeUtilization: 0.82,
      defaultPackingFactor: 0.65,
      defaultProtectiveClearanceIn: 0,
      note: 'Existing Inventory Engine V1 defaults; fulfillment calibration and owner approval remain required.'
    },

    availability: {
      sellableInventoryStatuses: ['READY', 'AVAILABLE'],
      unavailableInventoryStatuses: ['SOLD', 'RESERVED', 'HOLD', 'DAMAGED', 'ARCHIVED'],
      states: ['available', 'available_unverified', 'sold', 'unavailable', 'invalid', 'unknown']
    },

    fieldKeys: {
      family: ['family', 'cork_type'],
      sizeClass: ['size_class'],
      orientation: ['packing_orientation', 'orientation'],
      inventoryType: ['inventory_type'],
      inventoryStatus: ['inventory_status'],
      oneOfOne: ['one_of_one'],
      pieceId: ['piece_id'],
      weight: ['piece_weight'],
      maximumLength: ['maximum_length', 'length'],
      maximumWidth: ['maximum_width', 'width'],
      maximumHeight: ['maximum_height', 'height'],
      thickness: ['average_thickness', 'thickness', 'wall_thickness'],
      outerDiameter: ['maximum_diameter', 'outside_diameter', 'outer_diameter'],
      innerDiameter: ['interior_diameter', 'inner_diameter'],
      openingWidth: ['opening_width', 'clear_opening_width'],
      openingHeight: ['opening_height', 'clear_opening_height'],
      openingDiameter: ['opening_diameter', 'clear_opening'],
      curvature: ['curvature_rating', 'curvature'],
      surfaceArea: ['surface_area'],
      cavityVolume: ['cavity_volume'],
      branchDiameter: ['branch_diameter'],
      branchSegments: ['branch_segments'],
      packingVolume: ['estimated_packing_volume'],
      packingFactor: ['packing_factor'],
      minimumBox: ['minimum_box'],
      protectiveClearance: ['protective_clearance'],
      shippingBehavior: ['shipping_behavior'],
      suggestedUses: ['suggested_uses'],
      newArrival: ['new_arrival'],
      statementPiece: ['statement_piece'],
      oversized: ['oversized'],
      importBatch: ['import_collection_id', 'release_wave'],
      importBatchName: ['import_collection_name'],
      arrivalDate: ['arrival_date'],
      animalCompatibility: ['animal_compatibility'],
      enclosureCompatibility: ['enclosure_compatibility'],
      humiditySuitability: ['humidity_suitability'],
      buildRoles: ['build_roles'],
      badges: ['badges'],
      searchKeywords: ['search_keywords'],
      recommendationLabels: ['recommendation_labels'],
      video: ['product_video'],
      media360: ['media_360'],
      measurementOverlay: ['measurement_overlay']
    },

    mediaFallback: null
  };

  return config;
});
